import { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useCancelOta } from '../api/cancelOta';
import { getOtaExecutionHistory } from '../api/getOtaExecutionHistory';
import { getOtaFirmwareReleases } from '../api/getOtaFirmwareReleases';
import { useStartOta } from '../api/startOta';
import { OTA_EXECUTION_STATUS } from '../types';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

export const useBoxOtaView = (lpUI: string) => {
  // 1. Generic hooks
  const addToast = useToasts((state) => state.addItem);
  const queryClient = useQueryClient();

  // 2. Domain hooks — queries
  const { data: firmwareData, isLoading: isFirmwaresLoading } =
    getOtaFirmwareReleases();

  const { data: latestExecutionData } = getOtaExecutionHistory({
    lpUI,
    listRequest: { page_size: 1, page_offset: 0 },
  });

  // 2b. Domain hooks — mutations
  const { mutate: startOtaMutate, isPending: isStarting } = useStartOta({
    config: {
      onSuccess: (data) => {
        addToast(
          {
            msg: `OTA started — job ID: ${data.job_id}`,
            type: MessageType.Success,
          },
          'root',
          {}
        );
        queryClient.invalidateQueries({ queryKey: ['ota', 'history', lpUI] });
        queryClient.invalidateQueries({ queryKey: ['ota', 'active', lpUI] });
      },
      onError: () => {
        addToast(
          { msg: 'Failed to start OTA', type: MessageType.Error },
          'root',
          {}
        );
      },
    },
  });

  const { mutate: cancelOtaMutate, isPending: isCancelling } = useCancelOta({
    config: {
      onSuccess: () => {
        addToast(
          { msg: 'OTA cancelled', type: MessageType.Success },
          'root',
          {}
        );
        queryClient.invalidateQueries({ queryKey: ['ota', 'history', lpUI] });
        queryClient.invalidateQueries({ queryKey: ['ota', 'active', lpUI] });
      },
      onError: () => {
        addToast(
          { msg: 'Failed to cancel OTA', type: MessageType.Error },
          'root',
          {}
        );
      },
    },
  });

  // 3. Local state
  const [selectedFirmware, setSelectedFirmware] = useState<string | undefined>(
    undefined
  );

  // 4. Computed values
  const isOtaInProgress = useMemo(
    () =>
      latestExecutionData?.executions[0]?.status ===
      OTA_EXECUTION_STATUS.IN_PROGRESS,
    [latestExecutionData]
  );

  const firmwareOptions = useMemo(
    () =>
      firmwareData?.releases.map((r) => ({
        label: r.firmware_version,
        value: r.firmware_version,
      })) ?? [],
    [firmwareData]
  );

  // 5. Callbacks
  const handleStart = useCallback(() => {
    if (!selectedFirmware) return;
    startOtaMutate({ lpUI, firmwareVersion: selectedFirmware });
  }, [selectedFirmware, startOtaMutate, lpUI]);

  const handleCancel = useCallback(() => {
    cancelOtaMutate({ lpUI });
  }, [cancelOtaMutate, lpUI]);

  const handleRefreshHistory = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ota', 'history', lpUI] });
  }, [queryClient, lpUI]);

  return {
    firmwareOptions,
    isFirmwaresLoading,
    selectedFirmware,
    setSelectedFirmware,
    isOtaInProgress,
    isStarting,
    isCancelling,
    handleStart,
    handleCancel,
    handleRefreshHistory,
  };
};
